import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Youtube, Instagram } from "lucide-react";
import { Link } from "react-router-dom";

const Footer = () => {
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img src="/lovable-uploads/e96b7e90-3233-4052-88c0-b565f9c3ca04.png" alt="Sandhu Feeds" className="h-12 w-auto brightness-200" />
              <div>
                <h3 className="text-lg font-bold font-display">Sandhu Feeds</h3>
                <p className="text-xs opacity-60 font-body">Premium Poultry Nutrition</p>
              </div>
            </div>
            <p className="text-sm opacity-70 font-body leading-relaxed mb-4">
              Founded in 1984 by Mukhtiar Singh Sandhu. Today, the Sandhu Brothers lead North India's trusted poultry feed operation — ISO 9001:2015 certified, serving 10,000+ farmers across 20+ states.
            </p>
            <p className="text-xs opacity-40 font-body italic">
              "Nourishing flocks, empowering farmers."
            </p>
          </div>

          {/* Products */}
          <div>
            <h4 className="font-bold font-display text-lg mb-6">Products</h4>
            <ul className="space-y-3 text-sm font-body">
              <li className="text-xs uppercase tracking-wider opacity-40 font-semibold mt-2">Broiler</li>
              {["Pre-Starter", "Starter", "Finisher", "Concentrated Feed"].map((p, i) => (
                <li key={i}>
                  <Link to={`/product/${i + 1}`} className="opacity-70 hover:opacity-100 transition-opacity">Broiler {p}</Link>
                </li>
              ))}
              <li className="text-xs uppercase tracking-wider opacity-40 font-semibold mt-4">HIKE Economy</li>
              {[{ name: "HIKE Starter", id: 9 }, { name: "HIKE Finisher", id: 10 }].map((p) => (
                <li key={p.id}>
                  <Link to={`/product/${p.id}`} className="opacity-70 hover:opacity-100 transition-opacity">{p.name}</Link>
                </li>
              ))}
              <li className="text-xs uppercase tracking-wider opacity-40 font-semibold mt-4">Layer Crumbs</li>
              {[{ name: "Chick Crumbs", id: 5 }, { name: "Grower Crumbs", id: 6 }, { name: "Developer Crumbs", id: 7 }, { name: "Pre-Lay Crumbs", id: 8 }].map((p) => (
                <li key={p.id}>
                  <Link to={`/product/${p.id}`} className="opacity-70 hover:opacity-100 transition-opacity">{p.name}</Link>
                </li>
              ))}
              <li className="text-xs uppercase tracking-wider opacity-40 font-semibold mt-4">Layer Phase</li>
              {[{ name: "Phase - 1", id: 11 }, { name: "Phase - 2", id: 12 }, { name: "Phase - 3", id: 13 }].map((p) => (
                <li key={p.id}>
                  <Link to={`/product/${p.id}`} className="opacity-70 hover:opacity-100 transition-opacity">{p.name}</Link>
                </li>
              ))}
              <li className="text-xs uppercase tracking-wider opacity-40 font-semibold mt-4">Layer Concentrate</li>
              {[{ name: "Concentrate Phase - 1", id: 14 }, { name: "Concentrate Phase - 2", id: 15 }].map((p) => (
                <li key={p.id}>
                  <Link to={`/product/${p.id}`} className="opacity-70 hover:opacity-100 transition-opacity">{p.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold font-display text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3 text-sm font-body">
              {[
                { label: "Home", to: "/" },
                { label: "Products", to: "/products" },
                { label: "About Us", to: "/about" },
                { label: "Blog", to: "/blog" },
                { label: "FAQ", to: "/faq" },
                { label: "Gallery", to: "/gallery" },
                { label: "Downloads", to: "/downloads" },
                { label: "Careers", to: "/careers" },
              ].map((link, i) => (
                <li key={i}>
                  <Link to={link.to} className="opacity-70 hover:opacity-100 transition-opacity">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold font-display text-lg mb-6">Contact</h4>
            <div className="space-y-4 text-sm font-body">
              <div className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 opacity-60" />
                <span className="opacity-70">Jind Road, Assandh, District Karnal, Haryana 132039</span>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 flex-shrink-0 opacity-60" />
                <div className="opacity-70 space-y-1">
                  <p>+91 74199 00071</p>
                  <p>+91 74199 00072</p>
                  <p>+91 74199 00073</p>
                  <p>+91 74199 00074</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 flex-shrink-0 opacity-60" />
                <a href="mailto:info@sandhufeeds.com" className="opacity-70 hover:opacity-100 transition-opacity">info@sandhufeeds.com</a>
              </div>
            </div>

            {/* Social */}
            <div className="flex gap-3 mt-6">
              {[
                { icon: Facebook, url: "https://www.facebook.com/Sandhu.Poultry" },
                { icon: Twitter, url: "https://twitter.com" },
                { icon: Youtube, url: "https://youtube.com" },
                { icon: Linkedin, url: "https://in.linkedin.com/company/sandhu-feeds" },
                { icon: Instagram, url: "https://www.instagram.com/sandhu.feeds/" },
              ].map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors">
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Registration & Bottom */}
        <div className="mt-12 pt-8 border-t border-primary-foreground/10">
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-xs opacity-40 font-body mb-6">
            <span>ISO 9001:2015 Certified</span>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs opacity-50 font-body">© 2026 Sandhu Feeds. All rights reserved.</p>
            <div className="flex gap-6 text-xs font-body opacity-50">
              <Link to="/privacy-policy" className="hover:opacity-100 transition-opacity">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:opacity-100 transition-opacity">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
